import gql from "graphql-tag";
import { generateContentNodeFragments } from "@/abilities/documentation/helpers/documentation.ts";
import { SearchContext } from "@/shared/search/search.types.ts";

import {
    AssetFilter,
    ContentEdge,
    ContentFilter,
    ContentGroupBy,
    ContentGroupingCriteria,
    ContentSort,
    TotalCount,
} from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import { ContentGroupResult } from "@/shared/documentation/mapper/document-grouped.mapper.ts";

/**
 * A group of the group-items query: only its cursor and the requested page of hits are selected - the key,
 * teaser and total of the group are not repeated, they already come with the group list (`ContentGroup`).
 */
export type GroupItemsEntry = Pick<ContentGroupResult, "cursor"> & { hits?: ContentEdge[] };

/**
 * A `contentsGrouped`/`relatedContentsGrouped` result. The two queries select different parts of a group,
 * so the group shape is a parameter: `ContentGroup` for the group list, `GroupItemsEntry` for the hits of
 * a single group.
 */
export interface GroupedQueryResult<TGroup> {
    totalGroups?: TotalCount | null;
    groups?: TGroup[] | null;
}

/**
 * The raw data of the grouped queries. Which path is filled depends on the search context the query was
 * built for (see `generateContentsGroupedQuery`).
 */
export interface GroupedQueryData<TGroup> {
    contentsGrouped?: GroupedQueryResult<TGroup> | null;
    mechanic?: { relatedContentsGrouped?: GroupedQueryResult<TGroup> | null } | null;
    product?: { relatedContentsGrouped?: GroupedQueryResult<TGroup> | null } | null;
    article?: { relatedContentsGrouped?: GroupedQueryResult<TGroup> | null } | null;
    assets?: { assets: ({ node: { relatedContentsGrouped?: GroupedQueryResult<TGroup> | null } } | null)[] } | null;
}

/**
 * The variables both grouped queries declare - a context id is only declared by the matching variant, the
 * others are ignored.
 */
export interface GroupedQueryVariables {
    filter?: ContentFilter;
    /**
     * The grouping criteria is missing as long as its config has not resolved yet - the query stays
     * disabled until then (see `enabled`), so it is never sent without one.
     */
    groupBy: Omit<ContentGroupBy, "criteria"> & { criteria?: ContentGroupingCriteria | null };
    groupAfter?: string;
    assetFilter?: AssetFilter;
    productId?: string;
    mechanicId?: string;
    articleId?: string;
    acceptedLanguages?: string[];
}

/** Variables of the group list query - it pages the groups and declares no hit arguments at all. */
export interface GroupListQueryVariables extends GroupedQueryVariables {
    groupFirst?: number;
}

/**
 * Variables of the group-items query - it pins `groups(first: 1)`, so it declares no `$groupFirst`, and
 * pages the hits of that single group instead. `acceptedLanguages` is only declared when the display
 * config selects a localized datafield.
 */
export interface GroupItemsQueryVariables extends GroupedQueryVariables {
    sort?: ContentSort;
    hitsFirst?: number;
    hitsAfter?: string;
}

gql`
    query contentGroupingConfig($id: ID!) {
        contentGroupingConfig(id: $id) {
            id
            criteria
            enabled
        }
    }
`;

/**
 * The paginated groups of the group list.
 *
 * Only selections are shared between the query variants, and every variant keeps at least one literal
 * field per selection set: graphql-codegen strips template interpolations when it reads the documents, so
 * a fully interpolated selection set (or an interpolated variable definition) would make the operation
 * invalid and silently drop it from the generated types.
 */
const groupsSelection = `groups(first: $groupFirst, after: $groupAfter) {
    key {
        field
        value
        teasers {
          title(acceptedLanguages: $acceptedLanguages, length:256)
        }
    }
    teaser {
        ... on ContentMapTeasers {
            title(length: 256)
        }
        ... on TopicTeasers {
            title(length: 256)
        }
        title(length: 256)
    }
    total {
        count
        countRelation
    }
    cursor
}`;

/**
 * Builds the query of the group list itself, against the root field matching the search context
 * (`contentsGrouped`, or the `relatedContentsGrouped` of the context entity
 *
 * It deliberately selects **no** `hits`: the items of a group are only loaded once the user expands it
 * (see `generateGroupItemsQuery`). Fetching representative hits for every group would request documents
 * nobody looks at and makes the query - including its expensive `totalGroups` aggregation - much slower.
 * Without hits the query needs neither the display-config-driven node fragments nor a sort.
 *
 * @param context - The current search context, if the groups belong to a mechanic, asset, product or article.
 */
export const generateContentsGroupedQuery = (context?: SearchContext) => {
    if (context?.mechanic) {
        return gql`query mechanicContentsGrouped($mechanicId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $groupFirst: NonNegativeInt, $groupAfter: String, $acceptedLanguages: [LanguageWithWildcard!]) {
            mechanic(id: $mechanicId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    totalGroups {
                        count
                        countRelation
                    }
                    ${groupsSelection}
                }
            }
        }`;
    }

    if (context?.asset && context?.product) {
        return gql`query assetContentsGrouped($assetFilter: AssetFilter!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $groupFirst: NonNegativeInt, $groupAfter: String, $acceptedLanguages: [LanguageWithWildcard!]) {
            assets(filter: $assetFilter) {
                assets(first: 1) {
                    cursor
                    node {
                        id
                        relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                            totalGroups {
                                count
                                countRelation
                            }
                            ${groupsSelection}
                        }
                    }
                }
            }
        }`;
    }

    if (context?.product) {
        return gql`query productContentsGrouped($productId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $groupFirst: NonNegativeInt, $groupAfter: String, $acceptedLanguages: [LanguageWithWildcard!]) {
            product(id: $productId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    totalGroups {
                        count
                        countRelation
                    }
                    ${groupsSelection}
                }
            }
        }`;
    }

    if (context?.article) {
        return gql`query articleContentsGrouped($articleId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $groupFirst: NonNegativeInt, $groupAfter: String, $acceptedLanguages: [LanguageWithWildcard!]) {
            article(id: $articleId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    totalGroups {
                        count
                        countRelation
                    }
                    ${groupsSelection}
                }
            }
        }`;
    }

    return gql`query contentsGrouped($filter: ContentFilter, $groupBy: ContentGroupBy!, $groupFirst: NonNegativeInt, $groupAfter: String, $acceptedLanguages: [LanguageWithWildcard!]) {
        contentsGrouped(groupBy: $groupBy, filter: $filter) {
            totalGroups {
                count
                countRelation
            }
            ${groupsSelection}
        }
    }`;
};

/**
 * Builds the query that loads one page of hits of a *single* group, against the root field matching the
 * search context and with a dynamic node selection driven by the given display config datafields/
 * localization fragments and node fields.
 *
 * Compared to the group list query it drops `totalGroups` (an expensive distinct-count aggregation),
 * `key`, `teaser` and `total` - none of them change while paging inside a group, so requesting them again
 * would only cost backend time and payload size. Selecting less is safe because these requests get their
 * own cache entry (see `createScopedKeyArgs` in the client cache) and therefore never overlap with the
 * selection set of the running grouped search.
 *
 * @param dataFieldsFragment - The datafield fragments of the display config.
 * @param localizationDatafieldsFragment - The localized datafield fragments of the display config.
 * @param fields - The node fields of the display config.
 * @param context - The current search context, if the groups belong to a mechanic, asset, product or article.
 */
export const generateGroupItemsQuery = (
    dataFieldsFragment: Record<string, string[]>,
    localizationDatafieldsFragment: Record<string, string[]>,
    fields: Record<string, string>,
    context?: SearchContext
) => {
    const { contentMapFragment, topicFragment, acceptedLanguageParam } = generateContentNodeFragments(
        dataFieldsFragment,
        localizationDatafieldsFragment,
        fields
    );

    let rootContentVersion = "";

    // Workaround: a topic has no versionLabel of its own, so it is read from its root content map.
    // The mapper (mapHitToGroupedDocumentationItem) copies it onto the topic node for the display config.
    if (fields["contentMap"]?.includes("versionLabel") || fields["topic"]?.includes("versionLabel")) {
        rootContentVersion = `useContext {
            rootContentMap {
                versionLabel
            }
        }`;
    }

    /** The hits of a single group - the `groups` wrapper stays in every variant, see `groupsSelection`. */
    const hitsSelection = `hits(sort: $sort, first: $hitsFirst, after: $hitsAfter) {
        cursor
        node {
            id
            contentId
            locale
            mimeType
            version
            modificationDate
            ${rootContentVersion}
            ${contentMapFragment}
            ${topicFragment}
        }
    }`;

    if (context?.mechanic) {
        return gql`query mechanicContentsGroupedItems($mechanicId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $sort: [ContentSort!], $groupAfter: String, $hitsFirst: NonNegativeInt, $hitsAfter: String ${acceptedLanguageParam}) {
            mechanic(id: $mechanicId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    groups(first: 1, after: $groupAfter) {
                        cursor
                        ${hitsSelection}
                    }
                }
            }
        }`;
    }

    if (context?.asset && context?.product) {
        return gql`query assetContentsGroupedItems($assetFilter: AssetFilter!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $sort: [ContentSort!], $groupAfter: String, $hitsFirst: NonNegativeInt, $hitsAfter: String ${acceptedLanguageParam}) {
            assets(filter: $assetFilter) {
                assets(first: 1) {
                    cursor
                    node {
                        id
                        relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                            groups(first: 1, after: $groupAfter) {
                                cursor
                                ${hitsSelection}
                            }
                        }
                    }
                }
            }
        }`;
    }

    if (context?.product) {
        return gql`query productContentsGroupedItems($productId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $sort: [ContentSort!], $groupAfter: String, $hitsFirst: NonNegativeInt, $hitsAfter: String ${acceptedLanguageParam}) {
            product(id: $productId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    groups(first: 1, after: $groupAfter) {
                        cursor
                        ${hitsSelection}
                    }
                }
            }
        }`;
    }

    if (context?.article) {
        return gql`query articleContentsGroupedItems($articleId: String!, $filter: ContentFilter, $groupBy: ContentGroupBy!, $sort: [ContentSort!], $groupAfter: String, $hitsFirst: NonNegativeInt, $hitsAfter: String ${acceptedLanguageParam}) {
            article(id: $articleId) {
                id
                relatedContentsGrouped(groupBy: $groupBy, filter: $filter) {
                    groups(first: 1, after: $groupAfter) {
                        cursor
                        ${hitsSelection}
                    }
                }
            }
        }`;
    }

    return gql`query contentsGroupedItems($filter: ContentFilter, $groupBy: ContentGroupBy!, $sort: [ContentSort!], $groupAfter: String, $hitsFirst: NonNegativeInt, $hitsAfter: String ${acceptedLanguageParam}) {
        contentsGrouped(groupBy: $groupBy, filter: $filter) {
            groups(first: 1, after: $groupAfter) {
                cursor
                ${hitsSelection}
            }
        }
    }`;
};
